// src/components/Users/UserList.js

import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "../../../axiosConfig";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Table,
  Button,
  Input,
  Avatar,
  Tag,
  Pagination,
  Spin,
  message,
  Dropdown,
  Typography,
  Card,
  Grid,
} from "antd";
import {
  EllipsisOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  EyeOutlined,
  HistoryOutlined,
  PlusOutlined,
  LoginOutlined,
  UserOutlined,
  MailOutlined,
  IdcardOutlined,
  TeamOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import "./UserList.css";
import EditUserForm from "./EditUserForm";
import DeleteUserDialog from "./DeleteUserDialog";
import ViewUserDialog from "./ViewUserDialog";
import AddUserForm from "./AddUserForm";
import UserHistoryDialog from "./UserHistoryDialog";
import { useUser } from "../../../UserContext";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

function UserList() {
  const { user: currentUser, loginUser } = useUser();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [usuarios, setUsuarios] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [searchInput, setSearchInput] = useState(filtro); // Estado local para el input de búsqueda con debounce
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10); // Por defecto 10 filas por página
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userNameToDelete, setUserNameToDelete] = useState("");
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [listLoading, setListLoading] = useState(false); // Estado de carga agregado

  // Estilos para encabezados fijos usando CSS-in-JS
  const tableHeaderStyle = {
    position: "sticky",
    top: 0,
    background: "#f8fafc",
    zIndex: 1,
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
  };

  // Función para verificar permisos CORREGIDA
  const hasPermission = useCallback(
    (moduleKey, permission) => {
      const module = currentUser?.modulos?.find((m) => m.key === moduleKey);
      if (!module || module.estado !== "disponible") return false;

      return currentUser?.modulosPermisos?.some(
        (mp) => mp.modulo === moduleKey && mp.permiso === permission
      );
    },
    [currentUser]
  );

  /**
   * Función para obtener usuarios con manejo de carga y filtros de búsqueda.
   */
  const fetchUsuarios = useCallback(async () => {
    setListLoading(true); // Iniciar carga
    try {
      const params = {};
      if (filtro) params.search = filtro; // Pasar el parámetro de búsqueda al backend

      const response = await api.get("/usuarios", { params }); // Asegúrate de que esta sea la URL correcta
      setUsuarios(response.data);
    } catch (error) {
      console.error("Error al obtener los usuarios:", error);
      message.error("Error al obtener los usuarios"); // Mostrar mensaje de error
    } finally {
      setListLoading(false); // Finalizar carga
    }
  }, [filtro]);

  /**
   * Implementa el debounce para el campo de búsqueda.
   * Espera 800 ms después de la última escritura antes de actualizar el filtro.
   */
  useEffect(() => {
    const handler = setTimeout(() => {
      setFiltro(searchInput);
      setPage(1); // Reinicia la paginación al buscar
    }, 800); // 800 ms de retraso

    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  /**
   * Función para manejar cambios en el campo de búsqueda.
   * Actualiza el estado local `searchInput`.
   */
  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  /**
   * Obtiene los usuarios al montar el componente y cada vez que `filtro` cambia.
   */
  useEffect(() => {
    fetchUsuarios();
  }, [fetchUsuarios]);

  /**
   * Maneja el cambio de página en la paginación.
   */
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  /**
   * Maneja el cambio en el número de filas por página.
   */
  const handleRowsPerPageChange = (current, size) => {
    setRowsPerPage(size);
    setPage(1); // Reinicia la paginación al cambiar el número de filas por página
  };

  /**
   * Funciones para manejar acciones de los botones en la tabla.
   */
  const handleEditClick = useCallback((usuario) => {
    setSelectedUser(usuario);
    setEditDialogOpen(true);
  }, []);

  const handleViewClick = useCallback((usuario) => {
    setSelectedUser(usuario);
    setViewDialogOpen(true);
  }, []);

  const handleHistoryClick = useCallback((usuario) => {
    setSelectedUser(usuario);
    setHistoryDialogOpen(true);
  }, []);

  const handleOpenDeleteDialog = useCallback((usuario) => {
    setUserToDelete(usuario.id);
    setUserNameToDelete(usuario.nombre);
    setDeleteDialogOpen(true);
  }, []);

  const handleLoginAs = useCallback(
    async (usuario) => {
      // Verificar que el usuario actual sea administrador
      if (!currentUser?.es_administrador) {
        message.error("No tienes permisos para usar esta funcionalidad");
        return;
      }

      // Evitar iniciar sesión como el mismo usuario
      if (currentUser.id === usuario.id) {
        message.warning("Ya tienes iniciada sesión con este usuario");
        return;
      }

      try {
        const response = await api.post("/auth/login-as", {
          targetUserId: usuario.id,
        });

        if (response.data.accessToken && response.data.user) {
          // Usar loginUser del contexto para actualizar el estado correctamente
          loginUser(
            response.data.user,
            response.data.accessToken,
            response.data.refreshToken
          );

          // Disparar evento para actualizar otros componentes que escuchan cambios de tokens
          window.dispatchEvent(new Event("tokens_updated"));

          message.success(`Sesión iniciada como ${usuario.nombre}`);

          // Pequeña pausa para asegurar que el contexto se actualice
          setTimeout(() => {
            // Navegar al dashboard para refrescar la vista
            navigate("/modules/Dashboard");
          }, 100);
        }
      } catch (error) {
        console.error("Error al iniciar sesión como usuario:", error);
        if (error.response?.status === 403) {
          message.error("No tienes permisos para usar esta funcionalidad");
        } else if (error.response?.status === 404) {
          message.error("Usuario no encontrado");
        } else {
          message.error("Error al iniciar sesión como usuario");
        }
      }
    },
    [currentUser, loginUser, navigate]
  );

  /**
   * Define las columnas de la tabla.
   */
  const columns = useMemo(() => {
    return [
      {
        title: "Usuario",
        dataIndex: "nombre",
        key: "nombre",
        render: (text, usuario) => (
          <div className="user-info-container">
            {usuario.avatar ? (
              <Avatar
                src={usuario.avatar}
                size={40}
                className="user-avatar-img"
              />
            ) : (
              <div className="user-avatar">
                <UserOutlined />
              </div>
            )}
            <div className="user-info-details">
              <div className="user-name">{usuario.nombre}</div>
              <div className="user-email">{usuario.correo_electronico}</div>
            </div>
          </div>
        ),
        sorter: (a, b) => a.nombre.localeCompare(b.nombre),
      },
      {
        title: "Número de Documento",
        dataIndex: "numero_documento",
        key: "numero_documento",
        sorter: (a, b) =>
          (a.numero_documento || "").localeCompare(b.numero_documento || ""),
      },
      {
        title: "Área",
        dataIndex: "area",
        key: "area",
        sorter: (a, b) => a.area.localeCompare(b.area),
      },
      {
        title: "Cargo",
        dataIndex: "cargo",
        key: "cargo",
        sorter: (a, b) => a.cargo.localeCompare(b.cargo),
      },
      {
        title: "Rol",
        dataIndex: ["asignacionRol", "rol", "nombre_rol"],
        key: "rol",
        width: 180,
        render: (text, usuario) => {
          const rol = usuario?.asignacionRol?.rol?.nombre_rol;
          return rol ? (
            <Tag className="role-tag" title={rol}>
              {rol.length > 15 ? `${rol.substring(0, 15)}...` : rol}
            </Tag>
          ) : (
            <Tag className="role-tag-empty">Sin rol asignado</Tag>
          );
        },
        sorter: (a, b) => {
          const rolA = a?.asignacionRol?.rol?.nombre_rol || "";
          const rolB = b?.asignacionRol?.rol?.nombre_rol || "";
          return rolA.localeCompare(rolB);
        },
      },
      {
        title: "Fecha de Creación",
        dataIndex: "fecha_creacion",
        key: "fecha_creacion",
        width: 150,
        render: (text) => format(new Date(text), "PPP", { locale: es }),
        sorter: (a, b) =>
          new Date(a.fecha_creacion) - new Date(b.fecha_creacion),
      },
      {
        title: "Último Acceso",
        dataIndex: "ultimo_acceso",
        key: "ultimo_acceso",
        width: 170,
        render: (text) => format(new Date(text), "PPP p", { locale: es }),
        sorter: (a, b) => new Date(a.ultimo_acceso) - new Date(b.ultimo_acceso),
      },
      {
        title: "Estado",
        dataIndex: "estado",
        key: "estado",
        width: 120,
        render: (estado) => {
          if (typeof estado !== "boolean") {
            return <Tag className="status-tag-unknown">Desconocido</Tag>;
          }
          return (
            <Tag
              className={estado ? "status-tag-active" : "status-tag-suspended"}
            >
              {estado ? "Activo" : "Suspendido"}
            </Tag>
          );
        },
        sorter: (a, b) => {
          const estadoA = a.estado ? 1 : 0;
          const estadoB = b.estado ? 1 : 0;
          return estadoA - estadoB;
        },
      },
      {
        title: <span style={{ whiteSpace: "nowrap" }}>Acciones</span>,
        key: "acciones",
        render: (text, usuario) => {
          const items = [
            hasPermission("Usuarios", "VISUALIZAR") && {
              key: "ver",
              icon: <EyeOutlined />,
              label: "Ver Usuario",
              onClick: () => handleViewClick(usuario),
            },
            hasPermission("Usuarios", "EDITAR") && {
              key: "editar",
              icon: <EditOutlined />,
              label: "Editar Usuario",
              onClick: () => handleEditClick(usuario),
            },
            // Solo mostrar "Iniciar sesión" si es administrador y no es el mismo usuario
            currentUser?.es_administrador &&
              currentUser.id !== usuario.id && {
                key: "login-as",
                icon: <LoginOutlined />,
                label: "Iniciar sesión",
                onClick: () => handleLoginAs(usuario),
              },
            hasPermission("Usuarios", "ELIMINAR") && {
              key: "eliminar",
              icon: <DeleteOutlined />,
              label: "Eliminar Usuario",
              onClick: () => handleOpenDeleteDialog(usuario),
            },
            hasPermission("Usuarios", "VISUALIZAR") && {
              key: "historial",
              icon: <HistoryOutlined />,
              label: "Ver Historial",
              onClick: () => handleHistoryClick(usuario),
            },
          ].filter(Boolean);

          return (
            <div className="user-actions-dropdown">
              <Dropdown
                menu={{ items }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <Button
                  icon={<EllipsisOutlined />}
                  className="user-actions-btn"
                  type="text"
                />
              </Dropdown>
            </div>
          );
        },
        width: 100,
      },
    ];
  }, [
    handleEditClick,
    handleViewClick,
    handleOpenDeleteDialog,
    handleHistoryClick,
    handleLoginAs,
    hasPermission,
    currentUser,
  ]);

  /**
   * Define los usuarios paginados para mostrar en la tabla.
   */
  const paginatedUsuarios = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return usuarios.slice(startIndex, startIndex + rowsPerPage);
  }, [usuarios, page, rowsPerPage]);

  /**
   * Renderiza una tarjeta de usuario para vista móvil
   */
  const renderUserCard = (usuario) => {
    const items = [
      hasPermission("Usuarios", "VISUALIZAR") && {
        key: "ver",
        icon: <EyeOutlined />,
        label: "Ver Usuario",
        onClick: () => handleViewClick(usuario),
      },
      hasPermission("Usuarios", "EDITAR") && {
        key: "editar",
        icon: <EditOutlined />,
        label: "Editar Usuario",
        onClick: () => handleEditClick(usuario),
      },
      currentUser?.es_administrador &&
        currentUser.id !== usuario.id && {
          key: "login-as",
          icon: <LoginOutlined />,
          label: "Iniciar sesión",
          onClick: () => handleLoginAs(usuario),
        },
      hasPermission("Usuarios", "ELIMINAR") && {
        key: "eliminar",
        icon: <DeleteOutlined />,
        label: "Eliminar Usuario",
        onClick: () => handleOpenDeleteDialog(usuario),
      },
      hasPermission("Usuarios", "VISUALIZAR") && {
        key: "historial",
        icon: <HistoryOutlined />,
        label: "Ver Historial",
        onClick: () => handleHistoryClick(usuario),
      },
    ].filter(Boolean);

    return (
      <Card key={usuario.id} className="user-mobile-card">
        <div className="user-mobile-header">
          <div className="user-mobile-avatar">
            {usuario.avatar ? (
              <Avatar src={usuario.avatar} size={48} />
            ) : (
              <Avatar size={48} className="user-mobile-avatar-placeholder">
                <UserOutlined />
              </Avatar>
            )}
          </div>
          <div className="user-mobile-info">
            <div className="user-mobile-name">{usuario.nombre}</div>
            <div className="user-mobile-status">
              {typeof usuario.estado === "boolean" ? (
                <Tag
                  className={
                    usuario.estado
                      ? "status-tag-active"
                      : "status-tag-suspended"
                  }
                >
                  {usuario.estado ? "Activo" : "Suspendido"}
                </Tag>
              ) : (
                <Tag className="status-tag-unknown">Desconocido</Tag>
              )}
            </div>
          </div>
          <div className="user-mobile-actions">
            <Dropdown
              menu={{ items }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button
                icon={<EllipsisOutlined />}
                className="user-mobile-actions-btn"
                type="text"
              />
            </Dropdown>
          </div>
        </div>

        <div className="user-mobile-details">
          <div className="user-mobile-detail">
            <MailOutlined className="user-mobile-detail-icon" />
            <span className="user-mobile-detail-text">
              {usuario.correo_electronico}
            </span>
          </div>

          {usuario.numero_documento && (
            <div className="user-mobile-detail">
              <IdcardOutlined className="user-mobile-detail-icon" />
              <span className="user-mobile-detail-text">
                {usuario.numero_documento}
              </span>
            </div>
          )}

          <div className="user-mobile-detail">
            <TeamOutlined className="user-mobile-detail-icon" />
            <span className="user-mobile-detail-text">
              {usuario.area} - {usuario.cargo}
            </span>
          </div>

          <div className="user-mobile-detail">
            <span className="user-mobile-detail-label">Rol:</span>
            {usuario?.asignacionRol?.rol?.nombre_rol ? (
              <Tag
                className="role-tag"
                title={usuario.asignacionRol.rol.nombre_rol}
              >
                {usuario.asignacionRol.rol.nombre_rol.length > 20
                  ? `${usuario.asignacionRol.rol.nombre_rol.substring(
                      0,
                      20
                    )}...`
                  : usuario.asignacionRol.rol.nombre_rol}
              </Tag>
            ) : (
              <Tag className="role-tag-empty">Sin rol asignado</Tag>
            )}
          </div>

          <div className="user-mobile-dates">
            <div className="user-mobile-date">
              <CalendarOutlined className="user-mobile-date-icon" />
              <span className="user-mobile-date-text">
                Creado:{" "}
                {format(new Date(usuario.fecha_creacion), "dd/MM/yyyy", {
                  locale: es,
                })}
              </span>
            </div>
            <div className="user-mobile-date">
              <ClockCircleOutlined className="user-mobile-date-icon" />
              <span className="user-mobile-date-text">
                Último:{" "}
                {format(new Date(usuario.ultimo_acceso), "dd/MM/yyyy", {
                  locale: es,
                })}
              </span>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  /**
   * Determina si se debe mostrar la vista móvil
   */
  const isMobile = !screens.md;

  return (
    <div className="user-list-container">
      {/* Header Section */}
      <div className="user-list-header">
        <Title level={2} className="user-list-title">
          Gestión de Usuarios
        </Title>
        <Text className="user-list-subtitle">
          Administra los usuarios del sistema
        </Text>

        <div className="user-list-actions">
          <div className="user-list-search">
            <Input
              placeholder="Buscar por nombre, correo o documento"
              value={searchInput}
              onChange={handleSearchChange}
              suffix={<SearchOutlined />}
              size="large"
              allowClear
            />
          </div>

          {hasPermission("Usuarios", "CREAR") && (
            <Button
              className="user-list-add-btn"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => setAddDialogOpen(true)}
            >
              Agregar Usuario
            </Button>
          )}
        </div>
      </div>

      {/* Content Container */}
      <Spin
        spinning={listLoading}
        tip="Cargando usuarios..."
        className="user-list-loading"
      >
        {isMobile ? (
          /* Mobile Cards View */
          <div className="user-mobile-container">
            <div className="user-mobile-grid">
              {paginatedUsuarios.map((usuario) => renderUserCard(usuario))}
            </div>

            {/* Mobile Pagination */}
            <div className="user-mobile-pagination">
              <Pagination
                current={page}
                pageSize={rowsPerPage}
                total={usuarios.length}
                onChange={handleChangePage}
                showSizeChanger
                onShowSizeChange={(current, size) =>
                  handleRowsPerPageChange(current, size)
                }
                pageSizeOptions={["5", "10", "25"]}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} de ${total}`
                }
                size="small"
                simple
              />
            </div>
          </div>
        ) : (
          /* Desktop Table View */
          <div className="user-list-table-container">
            <Table
              className="user-list-table"
              columns={columns.map((col) => ({
                ...col,
                onHeaderCell: () => ({
                  style: tableHeaderStyle,
                }),
              }))}
              dataSource={paginatedUsuarios}
              pagination={false}
              rowKey="id"
              scroll={{ x: 1200, y: 600 }}
            />

            {/* Desktop Pagination */}
            <div className="user-list-pagination">
              <Pagination
                current={page}
                pageSize={rowsPerPage}
                total={usuarios.length}
                onChange={handleChangePage}
                showSizeChanger
                onShowSizeChange={(current, size) =>
                  handleRowsPerPageChange(current, size)
                }
                pageSizeOptions={["5", "10", "25", "50", "100"]}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} de ${total} usuarios`
                }
              />
            </div>
          </div>
        )}
      </Spin>

      {/* PROTEGER LOS DIÁLOGOS SEGÚN PERMISOS */}
      {editDialogOpen &&
        selectedUser &&
        hasPermission("Usuarios", "EDITAR") && (
          <EditUserForm
            user={selectedUser}
            open={editDialogOpen}
            handleClose={() => setEditDialogOpen(false)}
            fetchUsuarios={fetchUsuarios}
          />
        )}

      {viewDialogOpen &&
        selectedUser &&
        hasPermission("Usuarios", "VISUALIZAR") && (
          <ViewUserDialog
            user={selectedUser}
            open={viewDialogOpen}
            handleClose={() => setViewDialogOpen(false)}
          />
        )}

      {deleteDialogOpen &&
        userToDelete &&
        hasPermission("Usuarios", "ELIMINAR") && (
          <DeleteUserDialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            userId={userToDelete}
            userName={userNameToDelete}
            fetchUsuarios={fetchUsuarios}
          />
        )}

      {historyDialogOpen &&
        selectedUser &&
        hasPermission("Usuarios", "VISUALIZAR") && (
          <UserHistoryDialog
            userId={selectedUser.id}
            open={historyDialogOpen}
            handleClose={() => setHistoryDialogOpen(false)}
          />
        )}

      {addDialogOpen && hasPermission("Usuarios", "CREAR") && (
        <AddUserForm
          open={addDialogOpen}
          handleClose={() => setAddDialogOpen(false)}
          fetchUsuarios={fetchUsuarios}
        />
      )}
    </div>
  );
}

export default UserList;
